"use client";

import { useAuth } from "@/hooks/useAuth";
import React, { useState } from "react";
import { ModalWrapper } from "../ModalWrapper";
import { useAppStore } from "@/store/useAppStore";
import { usePathname } from "next/navigation";

export const AuthModal = () => {
  const {
    signIn,
    signUp,
    signInWithGoogle,
    resetPassword,
    isSupabaseConfigured,
  } = useAuth();
  const { setIsModalOpen, setActiveModal } = useAppStore();
  const pathname = usePathname();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(false);

  const getPageSpecificMessage = () => {
    // if (pathname === "/challenges") {
    //   return {
    //     title: isSignUp ? "Create Account" : "Sign in to start challenges",
    //   };
    // }
    // if (pathname === "/markets") {
    //   return {
    //     title: isSignUp ? "Create Account" : "Sign in to make a prediction",
    //   };
    // }
    return {
      title: isSignUp ? "Create Account" : "Sign In",
    };
  };

  const pageMessage = getPageSpecificMessage();

  const handleSignIn = async () => {
    setAuthError("");
    setSuccessMessage("");
    setIsAuthLoading(true);

    if (!email || !password) {
      setAuthError("Please enter email and password");
      setIsAuthLoading(false);
      return;
    }

    const { error } = await signIn(email, password);

    if (error) {
      setAuthError(error.message);
    } else {
      setIsModalOpen(false);
      setActiveModal(null);
      setEmail("");
      setPassword("");
    }

    setIsAuthLoading(false);
  };

  const handleSignUp = async () => {
    setAuthError("");
    setSuccessMessage("");
    setIsAuthLoading(true);

    if (!email || !password) {
      setAuthError("Please enter email and password");
      setIsAuthLoading(false);
      return;
    }

    const { error } = await signUp(email, password);

    if (error) {
      setAuthError(error.message);
    } else {
      setIsModalOpen(false);
      setActiveModal(null);
      setEmail("");
      setPassword("");
      if (isSupabaseConfigured) {
        setAuthError("");
        setIsModalOpen(true);
        setActiveModal("email");
      }
    }

    setIsAuthLoading(false);
  };

  const handleGoogleSignIn = async () => {
    setAuthError("");
    setSuccessMessage("");
    setIsAuthLoading(true);

    const { error } = await signInWithGoogle();

    if (error) {
      setAuthError(error.message);
      setIsAuthLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setAuthError("");
    setSuccessMessage("");
    setIsAuthLoading(true);

    if (!email) {
      setAuthError("Please enter your email address");
      setIsAuthLoading(false);
      return;
    }

    const { error } = await resetPassword(email);

    if (error) {
      setAuthError(error.message);
    } else {
      setSuccessMessage("Password reset email sent! Check your inbox.");

      setTimeout(() => {
        setIsForgotPassword(false);
        setSuccessMessage("");
        setEmail("");
      }, 3000);
    }

    setIsAuthLoading(false);
  };

  const onClose = () => {
    setIsModalOpen(false);
    setActiveModal(null);
    setEmail("");
    setPassword("");
    setAuthError("");
    setSuccessMessage("");
    setIsForgotPassword(false);
  };

  const resetForm = () => {
    setAuthError("");
    setSuccessMessage("");
    setPassword("");
  };

  return (
    <ModalWrapper>
      <div className="bg-gray-900/95 backdrop-blur-xl rounded-2xl p-6 max-w-md max-h-[70vh] overflow-y-auto border border-white/10 shadow-2xl m-4">
        <h2 className="text-2xl font-bold text-white mb-6">
          {isForgotPassword
            ? "Reset Password"
            : pageMessage.title}
        </h2>

        {!isSupabaseConfigured && (
          <div className="mb-4 p-3 bg-yellow-500/20 rounded-lg border border-yellow-500/30">
            <p className="text-sm text-yellow-300">
              ⚠️ Demo Mode - Any email/password works
            </p>
          </div>
        )}

        {authError && (
          <div className="mb-4 p-3 bg-red-500/20 rounded-lg border border-red-500/30">
            <p className="text-sm text-red-300 flex items-center">
              <span className="mr-2">❌</span>
              {authError}
            </p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-green-500/20 rounded-lg border border-green-500/30">
            <p className="text-sm text-green-300 flex items-center">
              <span className="mr-2">✅</span>
              {successMessage}
            </p>
          </div>
        )}

        <div className="space-y-4">
          {!isForgotPassword && (
            <>
              <button
                onClick={handleGoogleSignIn}
                disabled={isAuthLoading}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white/10 hover:bg-white/20 disabled:bg-white/5 disabled:cursor-not-allowed border border-white/20 rounded-lg text-white font-medium transition-all"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {isAuthLoading ? "Signing in..." : "Continue with Google"}
              </button>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-gray-900 text-gray-400">
                    Or continue with email
                  </span>
                </div>
              </div>
            </>
          )}

          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all"
          />

          {!isForgotPassword && (
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-teal focus:border-transparent transition-all"
            />
          )}

          {isForgotPassword ? (
            <>
              <button
                onClick={handleForgotPassword}
                disabled={isAuthLoading}
                className="w-full bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-semibold transition-all"
              >
                {isAuthLoading ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <span className="mr-2">📧</span>
                    Send
                  </span>
                )}
              </button>

              <div className="text-center">
                <button
                  onClick={() => {
                    setIsForgotPassword(false);
                    resetForm();
                  }}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  ← Back to Sign In
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={isSignUp ? handleSignUp : handleSignIn}
                disabled={isAuthLoading}
                className="w-full bg-brand-teal hover:bg-brand-teal disabled:bg-brand-teal/50 disabled:cursor-not-allowed text-white py-3 rounded-lg font-semibold transition-all"
              >
                {isAuthLoading ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Loading...
                  </span>
                ) : isSignUp ? (
                  "Sign Up"
                ) : (
                  "Sign In"
                )}
              </button>

              {!isSignUp && (
                <div className="text-center">
                  <button
                    onClick={() => {
                      setIsForgotPassword(true);
                      resetForm();
                    }}
                    className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>
              )}

              <div className="text-center">
                <button
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    resetForm();
                  }}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {isSignUp
                    ? "Already have an account? Sign in"
                    : "Don't have an account? Sign up"}
                </button>
              </div>
            </>
          )}

          <button
            onClick={onClose}
            className="w-full text-gray-400 hover:text-white text-sm transition-colors"
          >
            Cancel
          </button>
        </div>

        {isForgotPassword && isSupabaseConfigured && (
          <div className="mt-4 p-3 bg-blue-500/10 rounded-lg border border-blue-500/20">
            <p className="text-xs text-blue-300">
              💡 You'll receive an email with a link to reset your password. The
              link will redirect you back to complete the process.
            </p>
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};